<template>
  <section class="page" data-testid="page-users">
    <header class="page-header">
      <div><h1>用户画像</h1><p>手动维护用户特征、备注和分析结果。</p></div>
      <div class="actions">
        <button class="button" :disabled="!selected" @click="() => copyProfile()">复制画像</button>
        <button class="button" :disabled="!filteredUsers.length" @click="copyCurrentView">复制当前视图</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="metrics-grid">
      <MetricCard label="画像数" :value="users.length" />
      <MetricCard label="当前匹配" :value="filteredUsers.length" />
      <MetricCard label="低置信" :value="qualityMetrics.lowConfidence" />
      <MetricCard label="过期画像" :value="qualityMetrics.stale" />
      <MetricCard label="样本不足" :value="qualityMetrics.lowMessages" />
      <MetricCard label="老群友" :value="qualityMetrics.oldFriends" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>
    <div class="split">
      <div class="panel">
        <div class="toolbar">
          <input v-model="query" placeholder="搜索 QQ / 昵称 / 特征" @keyup.enter="() => load()" />
          <input v-model="directUserId" placeholder="直接打开 QQ" @keyup.enter="openDirectUser" />
          <button class="button" :class="{ primary: qualityFilter === 'all' }" @click="qualityFilter = 'all'">全部</button>
          <button class="button" :class="{ primary: qualityFilter === 'needs-work' }" @click="qualityFilter = 'needs-work'">待完善</button>
          <button class="button" :class="{ primary: qualityFilter === 'fresh' }" @click="qualityFilter = 'fresh'">新鲜</button>
          <button class="button" :class="{ primary: qualityFilter === 'stale' }" @click="qualityFilter = 'stale'">过期</button>
          <select v-model="relationshipFilter">
            <option value="">全部关系</option>
            <option value="new">新群友</option>
            <option value="acquaintance">有印象</option>
            <option value="familiar">熟人</option>
            <option value="old_friend">老群友</option>
          </select>
          <button class="button" :disabled="loading || !directUserId.trim()" @click="openDirectUser">打开</button>
        </div>
        <div v-if="!loading && !filteredUsers.length" class="empty">没有匹配的用户画像。</div>
        <DataTable v-else :columns="listColumns" :rows="filteredUsers" row-key="user_id">
          <template #nicknames="{ row }">{{ (row.nicknames || []).join(", ") || "-" }}</template>
          <template #traits="{ row }">{{ (row.traits || []).join(", ") }}</template>
          <template #profile_quality="{ row }">
            <div class="quality-cell">
              <span>{{ percent(row.profile_quality?.confidence) }} / {{ qualityLabel(row.profile_quality?.freshness) }}</span>
              <small>{{ (row.profile_quality?.signals || []).map(signalLabel).join(", ") || "质量正常" }}</small>
            </div>
          </template>
          <template #actions="{ row }">
            <div class="row-actions">
              <button class="button small" :disabled="loading" @click="select(row.user_id)">查看</button>
              <button class="button small" @click="copyProfile(row)">复制</button>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="panel" v-if="selected">
        <h2>{{ selected.nicknames?.[0] || selected.user_id }}</h2>
        <div class="runtime-grid">
          <div><span>置信度</span><strong>{{ percent(selected.profile_quality?.confidence) }}</strong></div>
          <div><span>熟悉度</span><strong>{{ relationshipLabel(selected.profile_quality?.relationship) }}</strong></div>
          <div><span>新鲜度</span><strong>{{ qualityLabel(selected.profile_quality?.freshness) }}</strong></div>
          <div><span>距上次</span><strong>{{ daysLabel(selected.profile_quality?.days_since_seen) }}</strong></div>
          <div><span>消息数</span><strong>{{ selected.message_count || 0 }}</strong></div>
          <div><span>证据</span><strong>{{ selected.profile_evidence?.sample_count || 0 }}</strong></div>
          <div><span>平均长度</span><strong>{{ formatNumber(selected.avg_msg_length) }}</strong></div>
        </div>
        <div class="profile-verdict" :class="selectedNeedsWork ? 'warning' : 'ok'">
          <strong>{{ selectedNeedsWork ? "画像需要补充" : "画像可用于人格化回复" }}</strong>
          <span>{{ selectedAdvice }}</span>
        </div>
        <div class="profile-evidence">
          <header>
            <span>画像证据</span>
            <strong>{{ evidenceLevelLabel(selected.profile_evidence?.evidence_level) }} · {{ selected.profile_evidence?.sample_count || 0 }}</strong>
          </header>
          <div v-if="!recentEvidence.length" class="evidence-empty">暂无证据</div>
          <div v-for="sample in recentEvidence" :key="`${sample.ts}-${sample.text}`" class="evidence-row">
            <div>
              <strong>{{ sample.nickname || selected.nicknames?.[0] || selected.user_id }}</strong>
              <span>{{ timeLabel(sample.ts) }}</span>
            </div>
            <p>{{ sample.text }}</p>
          </div>
        </div>
        <FormField label="特征"><input v-model="traitInput" placeholder="新增特征" /></FormField>
        <button class="button" :disabled="loading || !traitInput.trim()" @click="addTrait">添加特征</button>
        <FormField label="备注"><textarea v-model="selected.notes" /></FormField>
        <FormField label="风格摘要"><input v-model="selected.style_summary" /></FormField>
        <div class="chips"><span v-for="trait in selected.traits" :key="trait" @click="deleteTrait(trait)">{{ trait }}</span></div>
        <div class="profile-facts">
          <div><span>话题</span><strong>{{ (selected.topics || []).join(", ") || "-" }}</strong></div>
          <div><span>常用称呼</span><strong>{{ (selected.bot_names || []).join(", ") || "-" }}</strong></div>
          <div><span>活跃时段</span><strong>{{ (selected.active_hours || []).join(", ") || "-" }}</strong></div>
          <div><span>提问率</span><strong>{{ percent(selected.question_rate) }}</strong></div>
          <div><span>Emoji 率</span><strong>{{ percent(selected.emoji_rate) }}</strong></div>
          <div><span>质量信号</span><strong>{{ (selected.profile_quality?.signals || []).map(signalLabel).join(", ") || "-" }}</strong></div>
        </div>
        <pre class="context-preview">{{ selected.context_preview || "暂无上下文预览" }}</pre>
        <footer class="actions">
          <button class="button primary" :disabled="loading" @click="save">保存</button>
          <button class="button" :disabled="loading" @click="analyze">手动分析</button>
        </footer>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { api, postJson, putJson } from "../api/client";
import type { UserProfile } from "../api/types";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const toast = useToastStore();
const route = useRoute();
const query = ref("");
const directUserId = ref("");
const qualityFilter = ref<"all" | "needs-work" | "fresh" | "stale">("all");
const relationshipFilter = ref("");
const loading = ref(false);
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择画像内容");
const users = ref<UserProfile[]>([]);
const selected = ref<UserProfile | null>(null);
const traitInput = ref("");
const listColumns = [
  { key: "user_id", label: "QQ" },
  { key: "nicknames", label: "昵称" },
  { key: "message_count", label: "消息" },
  { key: "profile_quality", label: "质量" },
  { key: "traits", label: "特征" },
  { key: "actions", label: "操作" }
];
const filteredUsers = computed(() => {
  return users.value.filter((user) => {
    const quality = user.profile_quality;
    const needsWork = profileNeedsWork(user);
    if (qualityFilter.value === "needs-work" && !needsWork) return false;
    if (qualityFilter.value === "fresh" && quality?.freshness !== "fresh") return false;
    if (qualityFilter.value === "stale" && quality?.freshness !== "stale") return false;
    if (relationshipFilter.value && quality?.relationship !== relationshipFilter.value) return false;
    return true;
  });
});
const qualityMetrics = computed(() => ({
  lowConfidence: users.value.filter((user) => Number(user.profile_quality?.confidence || 0) < 0.5).length,
  stale: users.value.filter((user) => user.profile_quality?.freshness === "stale").length,
  lowMessages: users.value.filter((user) => (user.profile_quality?.signals || []).includes("low_messages")).length,
  oldFriends: users.value.filter((user) => user.profile_quality?.relationship === "old_friend").length
}));
const selectedNeedsWork = computed(() => selected.value ? profileNeedsWork(selected.value) : false);
const recentEvidence = computed(() => selected.value?.profile_evidence?.recent_samples || []);
const selectedAdvice = computed(() => {
  if (!selected.value) return "";
  const signals = selected.value.profile_quality?.signals || [];
  if (signals.includes("low_messages")) return "样本太少，先观察更多真实聊天再做强人格化判断。";
  if (signals.includes("no_traits")) return "缺少稳定特征，可从内容分析发现或手动补充。";
  if (signals.includes("no_style")) return "缺少风格摘要，建议手动分析或补充说话方式。";
  if (signals.includes("stale")) return "画像较久未更新，真实群聊行为可能已经变化。";
  return "特征、风格和新鲜度足够支撑更自然的点名与接话。";
});
function percent(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 100)}%` : "-";
}
function qualityLabel(value: unknown) {
  const labels: Record<string, string> = { fresh: "新鲜", cooling: "冷却", stale: "过期", unknown: "未知" };
  return labels[String(value)] || "-";
}
function relationshipLabel(value: unknown) {
  const labels: Record<string, string> = { new: "新群友", acquaintance: "有印象", familiar: "熟人", old_friend: "老群友" };
  return labels[String(value)] || "-";
}
function evidenceLevelLabel(value: unknown) {
  const labels: Record<string, string> = { none: "无证据", sparse: "证据少", enough: "证据足" };
  return labels[String(value)] || "-";
}
function signalLabel(value: unknown) {
  const labels: Record<string, string> = {
    low_messages: "样本少",
    no_traits: "无特征",
    no_style: "无风格",
    stale: "久未活跃",
    never_seen: "未见过",
    no_evidence: "无证据"
  };
  return labels[String(value)] || String(value);
}
function daysLabel(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  if (n < 1) return "今天";
  return `${Math.round(n)}d`;
}
function formatNumber(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(1) : "-";
}
function timeLabel(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "-";
  return new Date(n * 1000).toLocaleString();
}
function profileNeedsWork(user: UserProfile) {
  const signals = user.profile_quality?.signals || [];
  return Number(user.profile_quality?.confidence || 0) < 0.5 || user.profile_quality?.freshness === "stale" || signals.length > 0;
}
async function copyProfile(profile = selected.value) {
  if (!profile) return;
  await copyJson(profile, "用户画像已复制");
}
async function copyCurrentView() {
  await copyJson({
    filter: { query: query.value, quality: qualityFilter.value, relationship: relationshipFilter.value || "all" },
    metrics: qualityMetrics.value,
    users: filteredUsers.value
  }, "用户画像视图已复制");
}
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    const payload = await api<{ users: UserProfile[] }>(`/api/user-analysis/users?q=${encodeURIComponent(query.value)}&limit=200`);
    users.value = payload.users || [];
    if (announce) actionResult.value = `已加载 ${users.value.length} 个用户画像`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载用户画像失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function select(userId: number | string) {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await api<{ user: UserProfile }>(`/api/user-analysis/users/${userId}`);
    selected.value = payload.user;
    directUserId.value = String(payload.user.user_id || userId);
    actionResult.value = "用户画像已打开";
  } catch (err) {
    error.value = err instanceof Error ? err.message : "读取用户画像失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function openDirectUser() {
  const userId = directUserId.value.trim();
  if (!userId) return;
  await select(userId);
}
async function save() {
  if (!selected.value) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await putJson<{ user: UserProfile }>(`/api/user-analysis/users/${selected.value.user_id}`, selected.value);
    selected.value = payload.user;
    actionResult.value = "用户画像已保存";
    toast.push("已保存");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "保存用户画像失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function addTrait() {
  if (!selected.value || !traitInput.value.trim()) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<{ user: UserProfile }>(`/api/user-analysis/users/${selected.value.user_id}/traits`, { trait: traitInput.value.trim() });
    selected.value = payload.user;
    traitInput.value = "";
    actionResult.value = "特征已添加";
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "添加特征失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function deleteTrait(trait: string) {
  if (!selected.value) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await api<{ user: UserProfile }>(`/api/user-analysis/users/${selected.value.user_id}/traits/${encodeURIComponent(trait)}`, { method: "DELETE" });
    selected.value = payload.user;
    actionResult.value = "特征已删除";
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "删除特征失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function analyze() {
  if (!selected.value) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<{ user: UserProfile }>(`/api/user-analysis/users/${selected.value.user_id}/analyze`, {});
    selected.value = payload.user;
    actionResult.value = "用户画像分析完成";
    toast.push("分析完成");
  } catch (err) {
    error.value = err instanceof Error ? err.message : "分析用户画像失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
onMounted(async () => {
  await load();
  const userId = String(route.query.user_id || "").trim();
  if (userId) {
    directUserId.value = userId;
    await select(userId);
  }
});
</script>

<style scoped>
.runtime-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}
.runtime-grid div,
.profile-facts div {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px;
}
.runtime-grid span,
.profile-facts span {
  display: block;
  color: var(--muted);
  font-size: 12px;
}
.runtime-grid strong,
.profile-facts strong {
  display: block;
  margin-top: 5px;
  font-size: 14px;
}
.profile-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.context-preview {
  margin-top: 12px;
}

.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}

.quality-cell {
  display: grid;
  gap: 4px;
  min-width: 120px;
}

.quality-cell small {
  color: var(--muted);
}

.row-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.profile-verdict {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin-bottom: 12px;
}

.profile-verdict strong,
.profile-verdict span {
  display: block;
}

.profile-verdict span {
  color: var(--muted);
  margin-top: 5px;
}

.profile-verdict.ok {
  border-color: #386f47;
}

.profile-verdict.warning {
  border-color: #78612d;
}

.profile-evidence {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--panel-2);
  padding: 10px 12px;
  margin-bottom: 12px;
}

.profile-evidence header,
.evidence-row div {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.profile-evidence header span,
.evidence-row span,
.evidence-empty {
  color: var(--muted);
  font-size: 12px;
}

.evidence-row {
  border-top: 1px solid var(--border);
  padding-top: 10px;
  margin-top: 10px;
}

.evidence-row p {
  margin: 6px 0 0;
  color: var(--text);
  overflow-wrap: anywhere;
}

.evidence-empty {
  margin-top: 8px;
}
</style>
